import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import styles from './csv-import-review-body.css?inline';
import '../csv-import-validation-summary/csv-import-validation-summary.js';

import type { PreviewUploadView } from '../../types/views';
import {
  EMPTY_VALIDATION_SUMMARY,
  type ReviewValidationSummary,
} from '../csv-import-validation-summary/csv-import-validation-summary.js';

// ============================================================================
// Global augmentations
// ============================================================================

declare global {
  interface HTMLElementTagNameMap {
    'csv-import-review-body': CsvImportReviewBody;
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * Body content for the "Preview Upload" step of the CSV Import flow.
 *
 * Accepts a `view` prop of type `PreviewUploadView` computed by the
 * orchestrator. Summary counts are display data supplied by the orchestrator
 * and must stay consistent with the derived view. Replace the summary object
 * rather than mutating it in place so Lit observes the change.
 */
@customElement('csv-import-review-body')
export class CsvImportReviewBody extends LitElement {
  static readonly styles = [unsafeCSS(styles)];

  /** The fully-resolved view state, computed by the orchestrator. */
  @property({ type: String })
  view: PreviewUploadView = 'all-valid';

  /**
   * Summary counts for the uploaded CSV validation result.
   *
   * The orchestrator should pass a fresh object when counts change; Lit object
   * properties update by identity.
   */
  @property({ type: Object, attribute: false })
  summary: ReviewValidationSummary = EMPTY_VALIDATION_SUMMARY;

  /**
   * Screen-reader announcement for download status (success/failure).
   * Rendered into an aria-live region. Cleared by the orchestrator after 1s.
   */
  @property({ type: String, attribute: false })
  announcement: string | null = null;

  private get _summary(): ReviewValidationSummary {
    return { ...EMPTY_VALIDATION_SUMMARY, ...(this.summary ?? {}) };
  }

  private get _importableRows(): number {
    return this.view === 'all-invalid' ? 0 : this._summary.validCount;
  }

  private get _showReportButton(): boolean {
    const summary = this._summary;
    const rejectedRows = summary.invalidCount + summary.duplicateCount;
    return (
      this.view !== 'all-valid' &&
      this.view !== 'processing' &&
      rejectedRows > 0
    );
  }

  private get _showUploadNewFileButton(): boolean {
    // Error recovery views always show the button regardless of rejected row count.
    if (
      this.view === 'import-error' ||
      this.view === 'error-report-download-error'
    ) {
      return true;
    }
    // all-invalid: always show — the user uploaded a bad file and needs to retry.
    if (this.view === 'all-invalid') {
      return true;
    }
    // Processing views don't render summary at all
    if (this.view === 'processing' || this.view === 'processing-delay') {
      return false;
    }
    // all-valid: no errors to fix, hide the button
    if (this.view === 'all-valid') {
      return false;
    }
    // partial-valid: show if there are rejected rows
    const summary = this._summary;
    return summary.invalidCount + summary.duplicateCount > 0;
  }

  override render() {
    switch (this.view) {
      case 'all-invalid':
        return this._renderSummary();
      case 'error-report-download-error':
        return this._renderSummary({
          title: 'The error report could not be downloaded.',
          message: 'Please try again or submit a help ticket.',
        });
      case 'processing':
        return this._renderProcessing();
      case 'processing-delay':
        return this._renderProcessingDelay();
      case 'import-error':
        return this._renderSummary({
          title: 'We were unable to import your records.',
          message: 'Please try again or submit a help ticket.',
        });
      case 'all-valid':
      case 'partial-valid':
      default:
        return this._renderSummary();
    }
  }

  private _renderProcessing() {
    return html`
      <div class="body processing-body">
        <inmar-hc-spinner
          size="large"
          variant="light"
          aria-label="Processing upload"
        ></inmar-hc-spinner>
      </div>
    `;
  }

  private _renderProcessingDelay() {
    return html`
      <div class="body">
        <inmar-hc-alert
          type="notice"
          heading-text="Your upload is being processed."
          body-text="This may take a while due to the size of your data. We'll email you a report once it's complete."
        ></inmar-hc-alert>
        <inmar-hc-spinner
          class="processing-spinner"
          size="large"
          variant="light"
          aria-label="Processing upload"
        ></inmar-hc-spinner>
      </div>
    `;
  }

  private _renderSummary(alert?: { title: string; message: string }) {
    const summary = this._summary;

    return html`
      <div class="body">
        ${alert ? this._renderErrorAlert(alert.title, alert.message) : nothing}

        <section
          class="summary-section"
          aria-labelledby="validation-summary-title"
          aria-describedby="validation-summary-notice"
          aria-live="polite"
          aria-atomic="true"
        >
          <h2 id="validation-summary-title" class="summary-heading">
            <span class="summary-count"
              >${this._importableRows}/${summary.totalRows}</span
            >
            <span class="summary-label">total records will be imported</span>
          </h2>

          <csv-import-validation-summary .summary=${this.summary}>
            ${this._showReportButton
              ? html`
                  <inmar-hc-button
                    slot="actions"
                    class="report-button"
                    label="Error report"
                    variant="secondary"
                    size="sm"
                    @button-click=${this._onDownloadErrors}
                  >
                    <hcds-icon
                      slot="left-icon"
                      name="download"
                      family="classic"
                      icon-style="light"
                    ></hcds-icon>
                  </inmar-hc-button>
                `
              : nothing}
            ${this._showUploadNewFileButton
              ? html`
                  <inmar-hc-button
                    slot="actions"
                    class="upload-button"
                    label="Upload new file"
                    variant="tertiary"
                    size="sm"
                    @button-click=${this._onUploadNewFile}
                  >
                    <hcds-icon
                      slot="left-icon"
                      name="upload"
                      family="classic"
                      icon-style="light"
                    ></hcds-icon>
                  </inmar-hc-button>
                `
              : nothing}
          </csv-import-validation-summary>

          <p id="validation-summary-notice" class="notice">
            Records with errors and duplicates will not be imported. We may also
            discover additional errors and duplicates when the file is imported.
          </p>
        </section>

        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          class="sr-only"
        >
          ${this.announcement ?? ''}
        </div>
      </div>
    `;
  }

  private _renderErrorAlert(title: string, message: string) {
    return html`
      <div class="error-alert" role="alert">
        <div class="error-alert-heading">
          <span class="error-alert-icon" aria-hidden="true">
            <hcds-icon
              name="diamond-exclamation"
              family="classic"
              icon-style="light"
            ></hcds-icon>
          </span>
          <p class="error-alert-title">${title}</p>
        </div>
        <p class="error-alert-message">${message}</p>
      </div>
    `;
  }

  private _onUploadNewFile() {
    this.dispatchEvent(
      new CustomEvent('csv-import-upload-new-file', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onDownloadErrors() {
    this.dispatchEvent(
      new CustomEvent('csv-import-download-errors', {
        bubbles: true,
        composed: true,
      }),
    );
  }
}
